// Pointer-based drag-and-drop for the deck editor. One controller drives every
// card tile: a press that stays put is a click (add/remove), a press that moves
// past a small threshold lifts a floating ghost you can drop into any zone, pool
// → deck (add at an index), deck → deck (reorder / move between Main/Extra/Side),
// deck → pool (remove). Works with mouse, pen and touch; auto-scrolls the deck and
// pool lists near their edges. The view reads `dnd.drag` to render the ghost,
// drop-zone rings and the insertion caret.
import type { CardInfo } from "@ygoevo/yo2-data";

export type DeckZone = "main" | "extra" | "side";
export type DropZoneId = DeckZone | "pool";
export type DragOrigin = { kind: "pool" } | { kind: "deck"; zone: DeckZone; index: number };

export interface DndHandlers {
  /** May this card live in this deck zone (frame rules only, counts checked in add)? */
  canAccept(card: CardInfo, zone: DeckZone): boolean;
  /** Insert a fresh copy from the pool into a zone at the given index. */
  add(card: CardInfo, zone: DeckZone, index: number): void;
  /** Reorder within a zone or move between zones. */
  move(from: { zone: DeckZone; index: number }, to: { zone: DeckZone; index: number }): void;
  /** Drop a deck card back onto the pool, pull it out of the deck. */
  remove(zone: DeckZone, index: number): void;
  /** A press that never moved, behaves like the old click (add / remove). */
  click(origin: DragOrigin, card: CardInfo): void;
}

export interface DragState {
  card: CardInfo;
  origin: DragOrigin;
  x: number; y: number;   // live pointer (viewport coords) for the ghost
  ox: number; oy: number; // grab offset inside the source tile
  w: number; h: number;   // source tile size → ghost size
  over: DropZoneId | null;
  index: number;          // insertion index inside `over` (deck zones only)
  accept: boolean;        // is the current hover a legal drop?
  caret: { left: number; top: number; h: number } | null; // insertion marker, zone-relative
  moved: boolean;         // past the drag threshold (vs. a click)
}

const THRESHOLD = 6; // px of travel before a press becomes a drag
const EDGE = 60;     // auto-scroll trigger band
const SPEED = 18;    // auto-scroll px/frame at the edge

export function createDnd(handlers: DndHandlers) {
  let active = $state<DragState | null>(null);
  let start: { x: number; y: number } | null = null;
  let raf = 0;

  function resolve() {
    if (!active) return;
    const under = document.elementFromPoint(active.x, active.y) as HTMLElement | null;
    const zoneEl = under?.closest<HTMLElement>("[data-dropzone]");
    if (!zoneEl) { active.over = null; active.accept = false; active.caret = null; return; }
    const zone = zoneEl.dataset.dropzone as DropZoneId;
    active.over = zone;

    if (zone === "pool") {
      // Only a card already in the deck can be removed by dropping on the pool.
      active.accept = active.origin.kind === "deck";
      active.index = -1;
      active.caret = null;
      return;
    }

    active.accept = handlers.canAccept(active.card, zone);
    const tiles = [...zoneEl.querySelectorAll<HTMLElement>("[data-tile]")];
    if (tiles.length === 0) { active.index = 0; active.caret = null; return; }

    // Nearest-tile hit test → insert before/after by which side of its centre.
    const zr = zoneEl.getBoundingClientRect();
    let idx = tiles.length, best = Infinity, mark = tiles[0]!, after = true;
    for (let i = 0; i < tiles.length; i++) {
      const t = tiles[i]!;
      const r = t.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const d = Math.hypot(active.x - cx, active.y - (r.top + r.height / 2));
      if (d < best) { best = d; after = active.x > cx; idx = after ? i + 1 : i; mark = t; }
    }
    active.index = idx;
    const r = mark.getBoundingClientRect();
    active.caret = { left: (after ? r.right : r.left) - zr.left, top: r.top - zr.top, h: r.height };
  }

  function autoScroll() {
    if (active?.moved) {
      const under = document.elementFromPoint(active.x, active.y) as HTMLElement | null;
      const sc = under?.closest<HTMLElement>("[data-dropscroll]");
      if (sc) {
        const r = sc.getBoundingClientRect();
        let dy = 0;
        if (active.y < r.top + EDGE) dy = -SPEED * Math.min(1, (r.top + EDGE - active.y) / EDGE);
        else if (active.y > r.bottom - EDGE) dy = SPEED * Math.min(1, (active.y - (r.bottom - EDGE)) / EDGE);
        if (dy) { sc.scrollTop += dy; resolve(); }
      }
    }
    raf = requestAnimationFrame(autoScroll);
  }

  function onMove(e: PointerEvent) {
    if (!active) return;
    active.x = e.clientX;
    active.y = e.clientY;
    if (!active.moved) {
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) < THRESHOLD) return;
      active.moved = true;
      document.body.style.cursor = "grabbing";
    }
    e.preventDefault(); // stop touch-scroll / text selection mid-drag
    resolve();
  }

  function onUp() {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    cancelAnimationFrame(raf);
    document.body.style.cursor = "";
    const a = active;
    active = null;
    start = null;
    if (!a) return;
    if (!a.moved) { handlers.click(a.origin, a.card); return; }
    if (!a.over || !a.accept) return; // dropped on empty space → snap back

    if (a.over === "pool") {
      if (a.origin.kind === "deck") handlers.remove(a.origin.zone, a.origin.index);
      return;
    }
    if (a.origin.kind === "pool") {
      handlers.add(a.card, a.over, a.index);
      return;
    }
    // deck → deck: removing the source first shifts later indices in the same zone.
    let to = a.index;
    if (a.origin.zone === a.over && a.index > a.origin.index) to -= 1;
    if (a.origin.zone === a.over && to === a.origin.index) return; // no-op
    handlers.move({ zone: a.origin.zone, index: a.origin.index }, { zone: a.over, index: to });
  }

  function begin(e: PointerEvent, card: CardInfo, origin: DragOrigin) {
    if (e.button != null && e.button !== 0) return; // left / primary only
    const tile = e.currentTarget as HTMLElement;
    const r = tile.getBoundingClientRect();
    start = { x: e.clientX, y: e.clientY };
    active = {
      card, origin,
      x: e.clientX, y: e.clientY,
      ox: e.clientX - r.left, oy: e.clientY - r.top,
      w: r.width, h: r.height,
      over: null, index: -1, accept: false, caret: null, moved: false,
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    raf = requestAnimationFrame(autoScroll);
  }

  /** True when this exact deck slot is the one being dragged (render it as a gap). */
  function isLifting(zone: DeckZone, index: number): boolean {
    return !!active?.moved && active.origin.kind === "deck" && active.origin.zone === zone && active.origin.index === index;
  }

  return {
    get drag() { return active; },
    begin,
    isLifting,
  };
}
